import React, {FC, useRef, useState} from "react";
import {useCurrentShow, useCurrentShowId, useExhibitor} from "hooks";
import Box from "@material-ui/core/Box";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import DotsMenuIcon from "view/images/DotsMenuIcon";
import Typography from "@material-ui/core/Typography";
import FlexBox from "view/components/Layout/FlexBox";
import SSOVisit from "view/components/AppBar/components/SwitchApp/SSOVisit";
import useCurrentExhibitorPairId from "hooks/useCurrentExhibitorPairId";
import useCurrentExhibitorPair from "hooks/useCurrentExhibitorPair";
import useAspSsoLink from "hooks/useAspSsoLink";
import {
  AditusType,
  ExhibitorUserConnectionType,
  ExpoPlatformType,
  SitkaType,
  VisitType,
} from "@evennt-tech/api-v2-core";
import SSOAditus from "view/components/AppBar/components/SwitchApp/SSOAditus";
import SSOSitka from "view/components/AppBar/components/SwitchApp/SSOSitka";
import SSOJet from "view/components/AppBar/components/SwitchApp/SSOJet";
import aspLogo from "assets/images/show/aspLogo.jpg";
import useStyles from "./styles";
import ExpoPlatformButton from "./ExpoPlatformButton";

type SwitchAppProps = {
  headerBackground: boolean;
};

const SwitchApp: FC<SwitchAppProps> = ({headerBackground}) => {
  const [show] = useCurrentShow();

  const classes = useStyles({headerBackground});
  const [isOpen, setIsOpen] = useState(false);

  const showId = useCurrentShowId();
  const exhibitorPairId = useCurrentExhibitorPairId();

  const [exhibitorPair, exhibitorConnection] = useCurrentExhibitorPair({
    showId,
    exhibitorPairId,
  });

  const exhibitor = useExhibitor();

  const anchorEl = useRef<Element>(null);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const [aspSsoLink] = useAspSsoLink(showId);

  if (!exhibitor || !show) {
    return null;
  }
  const handleAppRedirect = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    const noRedirect = e.currentTarget.href === `${window.location.href}#`;
    if (noRedirect) {
      alert("Something went wrong. Please try again later.");
    }
    window.open(e.currentTarget.href, "_blank");
  };

  const renderTooltipContent = () => {
    if (!exhibitor) {
      return null;
    }

    const companyId = exhibitorPair?.companyToShow?.company?.id;
    const visitConnectionRefIds =
      show.visit?.type === VisitType.Pushing && companyId
        ? {
            companyId,
            standId: exhibitorPair?.stand?.standId as string,
            exhibitorId: exhibitor.id,
          }
        : undefined;
    return (
      <FlexBox alignCenter gap={1}>
        {aspSsoLink && (
          <Box className={classes.tooltipAppItem}>
            <a
              className={classes.tooltipItemLink}
              href={aspSsoLink}
              target="_blank"
              rel="noreferrer"
              onClick={handleAppRedirect}
            >
              <Box className={classes.imageBox}>
                <img src={aspLogo} alt="ASP" />
              </Box>
              <Typography>ASP</Typography>
            </a>
          </Box>
        )}
        {show.expoPlatform?.type === ExpoPlatformType.Pushing && (
          <ExpoPlatformButton
            showId={show.id}
            exhibitorPairId={exhibitorPairId}
          />
        )}
        {show.aditus?.type === AditusType.Pushing &&
          (!exhibitorConnection?.contractorCompanyName ||
            exhibitorConnection?.role ===
              ExhibitorUserConnectionType.PRIMARY) && (
            <SSOAditus showId={showId} exhibitorPairId={exhibitorPairId} />
          )}
        {visitConnectionRefIds &&
          show.visit?.type === VisitType.Pushing &&
          (!exhibitorConnection?.contractorCompanyName ||
            exhibitorConnection?.role ===
              ExhibitorUserConnectionType.PRIMARY) && (
            <SSOVisit showId={show.id} exhibitorPairId={exhibitorPairId} />
          )}
        {show.sitka?.type === SitkaType.SSO &&
          (!exhibitorConnection?.contractorCompanyName ||
            exhibitorConnection?.role ===
              ExhibitorUserConnectionType.PRIMARY) && (
            <SSOSitka showId={showId} exhibitorPairId={exhibitorPairId} />
          )}
        {/* No contractor/role guard: the JET link works for every user on the
            pair. SSOJet renders nothing until a magic link exists. */}
        <SSOJet showId={showId} exhibitorPairId={exhibitorPairId} />
      </FlexBox>
    );
  };

  return (
    <>
      <IconButton
        buttonRef={anchorEl}
        onClick={handleOpen}
        className={classes.dotsMenuIcon}
      >
        <DotsMenuIcon />
      </IconButton>
      <Popover
        open={isOpen}
        onClose={handleClose}
        classes={{paper: classes.tooltipContainer}}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {renderTooltipContent()}
      </Popover>
    </>
  );
};

export default SwitchApp;
